import type { ColumnDef } from "@tanstack/react-table"
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table"
import {
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  MoreHorizontal,
} from "lucide-react"
import * as React from "react"
import { useState } from "react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import type { Filter, FilterValue } from "@/components/ui/data-table-filters"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { PagyPagination } from "@/components/ui/pagy-pagination"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  pagy?: {
    page: number
    items: number
    count: number
    pages: number
    prev: number | null
    next: number | null
  }
  onPageChange?: (page: number) => void
  emptyComponent?: React.ReactNode
  title?: string
  search?: {
    value: string
    onChange: (value: string) => void
    placeholder?: string
  }
  filters?: Filter[]
  filterValues?: FilterValue
  onFilterChange?: (values: FilterValue) => void
  actions?: Action<TData>[]
  embedded?: boolean
  deleteLabel?: string
  deleteMessage?: string
  // Server-side sort, ransack-style: "<column> asc|desc". Columns opt in
  // with `enableSorting: true` in their ColumnDef.
  sort?: string | null
  onSortChange?: (sort: string) => void
}

export interface Column<T> {
  header: string
  accessorKey: keyof T
}

export interface Action<T> {
  label: string
  icon?: React.ReactNode
  onClick: (item: T) => void
  variant?: "default" | "destructive"
  visible?: (item: T) => boolean
  // When this returns true the action renders disabled; `disabledReason` (if
  // set) shows as a tooltip explaining why.
  disabled?: (item: T) => boolean
  disabledReason?: string
}

export function DataTable<TData, TValue>({
  columns,
  data,
  pagy,
  onPageChange,
  emptyComponent,
  title,
  actions,
  embedded = false,
  deleteMessage,
  deleteLabel,
  sort,
  onSortChange,
}: DataTableProps<TData, TValue>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: !!pagy,
    pageCount: pagy?.pages,
  })

  const [selectedItem, setSelectedItem] = useState<TData | null>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleteAction, setDeleteAction] = useState<Action<TData> | null>(null)

  React.useEffect(() => {
    if (pagy && onPageChange) {
      table.setPageIndex(pagy.page - 1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync only when the server page changes; `table` is a new object every render
  }, [pagy?.page])

  const handleActionClick = (action: Action<TData>, item: TData) => {
    if (action.variant === "destructive") {
      setSelectedItem(item)
      setDeleteAction(action)
      setShowDeleteDialog(true)
    } else {
      action.onClick(item)
    }
  }

  const handleDeleteConfirm = () => {
    if (selectedItem && deleteAction) {
      deleteAction.onClick(selectedItem)
      setShowDeleteDialog(false)
      setSelectedItem(null)
      setDeleteAction(null)
    }
  }

  const [sortColumn, sortDirection] = (sort ?? "").split(" ")

  const handleSortClick = (columnId: string) => {
    if (!onSortChange) return
    const direction =
      sortColumn === columnId && sortDirection === "asc" ? "desc" : "asc"
    onSortChange(`${columnId} ${direction}`)
  }

  const sortIcon = (columnId: string) => {
    if (sortColumn !== columnId)
      return <ChevronsUpDown className="text-muted-foreground/50 size-3.5" />
    return sortDirection === "asc" ? (
      <ChevronUp className="size-3.5" />
    ) : (
      <ChevronDown className="size-3.5" />
    )
  }

  return (
    <div className="w-full">
      {title && <h1 className="mb-4 text-2xl font-semibold">{title}</h1>}
      {!data.length && emptyComponent ? (
        emptyComponent
      ) : (
        <>
          <div className={embedded ? "overflow-hidden rounded-md border" : ""}>
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className="bg-muted hover:bg-muted"
                  >
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        className={embedded ? "h-8 py-2" : ""}
                      >
                        {header.isPlaceholder ? null : onSortChange &&
                          header.column.columnDef.enableSorting === true ? (
                          <button
                            type="button"
                            className="hover:text-foreground flex items-center gap-1.5"
                            onClick={() => handleSortClick(header.column.id)}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                            {sortIcon(header.column.id)}
                          </button>
                        ) : (
                          flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )
                        )}
                      </TableHead>
                    ))}
                    {actions && (
                      <TableHead
                        className={embedded ? "h-8 w-[50px] py-2" : "w-[50px]"}
                      ></TableHead>
                    )}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                      {actions && (
                        <TableCell>
                          {(() => {
                            const visibleActions = actions.filter(
                              (a) => !a.visible || a.visible(row.original),
                            )
                            if (visibleActions.length === 0) return null
                            return (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    className="h-8 w-8 p-0"
                                  >
                                    <span className="sr-only">Open menu</span>
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {visibleActions.map((action) => {
                                    const isDisabled =
                                      action.disabled?.(row.original) ?? false
                                    const item = (
                                      <DropdownMenuItem
                                        disabled={isDisabled}
                                        onClick={() =>
                                          handleActionClick(
                                            action,
                                            row.original,
                                          )
                                        }
                                        className={
                                          action.variant === "destructive"
                                            ? "text-destructive"
                                            : ""
                                        }
                                      >
                                        {action.icon && (
                                          <span className="mr-2">
                                            {action.icon}
                                          </span>
                                        )}
                                        {action.label}
                                      </DropdownMenuItem>
                                    )

                                    if (isDisabled && action.disabledReason) {
                                      return (
                                        <Tooltip key={action.label}>
                                          <TooltipTrigger asChild>
                                            {/* Disabled items don't receive
                                                pointer events, so the wrapper
                                                span anchors the tooltip. */}
                                            <span className="block">
                                              {item}
                                            </span>
                                          </TooltipTrigger>
                                          <TooltipContent side="left">
                                            {action.disabledReason}
                                          </TooltipContent>
                                        </Tooltip>
                                      )
                                    }

                                    return (
                                      <span key={action.label}>{item}</span>
                                    )
                                  })}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )
                          })()}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + (actions ? 1 : 0)}
                      className="h-24 text-center"
                    >
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {pagy && (
            <div className="flex items-center gap-2 px-2 py-4">
              <div className="text-muted-foreground w-48 text-sm">
                Page {pagy.page} of {pagy.pages} ({pagy.count} total)
              </div>
              <PagyPagination
                page={pagy.page}
                pages={pagy.pages}
                onPageChange={onPageChange!}
              />
            </div>
          )}
        </>
      )}

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteMessage ??
                "This action cannot be undone. This will permanently delete the record."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive hover:bg-destructive/90 text-white"
            >
              {deleteLabel ?? "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
